import React, { useEffect, useState, useRef, useMemo } from "react";
import api from "@/lib/api";
import DashboardShell from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Upload, ShieldCheck, Trash2, MessageCircle, Layers, Search, Globe2, Lock, Link as LinkIcon, MoreVertical, Pencil, User } from "lucide-react";
import { formatPrice } from "@/components/ProjectCard";
import MagicDropPanel from "@/components/MagicDropPanel";
import BuilderURLPanel from "@/components/BuilderURLPanel";
import { useAuth } from "@/lib/auth";

export default function Listings() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [listings, setListings] = useState([]);
  const [rawText, setRawText] = useState("");
  const [extracted, setExtracted] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [consent, setConsent] = useState({});
  const [ocrResult, setOcrResult] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [variantFilter, setVariantFilter] = useState("All");
  const [search, setSearch] = useState("");
  const fileRef = useRef();

  // ⋮ menu — edit + delete
  const [editing, setEditing] = useState(null); // full listing being edited (null = closed)
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deletingBusy, setDeletingBusy] = useState(false);

  const canEditRow = (l) => isOwner || l.user_id === user?.user_id;

  const reload = async () => { const { data } = await api.get("/listings"); setListings(data); };
  useEffect(() => { reload(); }, []);

  const availableVariants = useMemo(() => {
    const s = new Set();
    listings.forEach((l) => { if (l.unit_variant) s.add(l.unit_variant); });
    return [...s].sort();
  }, [listings]);

  const visibleListings = useMemo(() => {
    const term = search.trim().toLowerCase();
    return listings.filter((l) => {
      if (variantFilter !== "All" && (l.unit_variant || "") !== variantFilter) return false;
      if (!term) return true;
      const hay = `${l.bhk || ""} ${l.property_type || ""} ${l.locality || ""} ${l.unit_variant || ""} ${l.notes || ""} ${l.external_rera_number || ""}`.toLowerCase();
      return hay.includes(term);
    });
  }, [listings, variantFilter, search]);

  const extract = async () => {
    if (!rawText.trim()) { toast.error("Paste a forwarded WhatsApp message first."); return; }
    setExtracting(true); setExtracted(null);
    try {
      const { data } = await api.post("/listings/whatsapp-extract", { raw_text: rawText });
      setExtracted(data);
      toast.success("Draft listing created. Review PII redaction and confirm consent to publish.");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Extraction failed."); }
    finally { setExtracting(false); }
  };

  const publish = async (id) => {
    if (!consent[id]) { toast.error("You must confirm consent before publishing."); return; }
    try { await api.post(`/listings/${id}/publish`, { consent_confirmed: true }); toast.success("Listing published."); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Publish failed."); }
  };

  const remove = async (id) => { try { await api.delete(`/listings/${id}`); reload(); } catch { toast.error("Could not delete."); } };

  // ⋮ menu handlers
  const openEdit = (l) => {
    setEditing(l);
    setEditForm({
      property_type: l.property_type || "",
      bhk: l.bhk || "",
      price_lakhs: l.price_lakhs != null ? String(l.price_lakhs) : "",
      locality: l.locality || "",
      unit_variant: l.unit_variant || "",
      carpet_area_sqft: l.carpet_area_sqft != null ? String(l.carpet_area_sqft) : "",
      construction_status: l.construction_status || "",
      notes: l.notes || "",
    });
  };
  const closeEdit = () => { setEditing(null); setEditForm({}); };
  const saveEdit = async () => {
    if (!editing) return;
    setSavingEdit(true);
    try {
      const payload = {
        ...editForm,
        price_lakhs: editForm.price_lakhs === "" ? null : Number(editForm.price_lakhs),
        carpet_area_sqft: editForm.carpet_area_sqft === "" ? null : Number(editForm.carpet_area_sqft),
      };
      // Strip empty strings (keep 0 and null intentionally)
      Object.keys(payload).forEach((k) => { if (payload[k] === "" || payload[k] === null) delete payload[k]; });
      await api.patch(`/listings/${editing.listing_id}`, payload);
      toast.success("Listing updated.");
      closeEdit();
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed.");
    } finally { setSavingEdit(false); }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeletingBusy(true);
    try {
      await api.delete(`/listings/${pendingDelete.listing_id}`);
      toast.success("Listing deleted.");
      setPendingDelete(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    } finally { setDeletingBusy(false); }
  };

  const toggleVisibility = async (l) => {
    const next = l.visibility_scope === "Portal_Global" ? "Agent_Private" : "Portal_Global";
    try {
      await api.post(`/listings/${l.listing_id}/visibility`, { visibility_scope: next });
      toast.success(
        next === "Portal_Global"
          ? "Now visible on the public INPropPortal.com /buy marketplace."
          : "Reverted to Agent Private — only shows on your branded portal."
      );
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Visibility update failed.");
    }
  };

  const uploadOcr = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/(png|jpeg|jpg|webp)/i.test(file.type)) { toast.error("Only PNG / JPEG / WEBP supported."); return; }
    setOcrLoading(true); setOcrResult(null);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result.split(",")[1]);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const { data } = await api.post("/listings/floor-plan-extract", { image_base64: b64, mime_type: file.type });
      setOcrResult(data);
      toast.success("Floor plan extracted. File auto-purges in 1 hour.");
    } catch (e2) { toast.error(e2?.response?.data?.detail || "OCR failed."); }
    finally { setOcrLoading(false); }
  };

  return (
    <DashboardShell>
      <div className="overline text-sg-gold-deep">SECONDARY MARKET · ZERO-FORM INGESTION</div>
      <h1 className="font-display text-4xl lg:text-5xl text-sg-navy mt-2">
        Listings & AI <span className="font-serif-italic text-sg-gold-deep">Ingestion.</span>
      </h1>
      <p className="text-sg-mute mt-2 max-w-2xl">
        Manual web forms are disabled. Use WhatsApp paste or OCR upload. Edge redaction strips PII before storage.
      </p>

      <Tabs defaultValue="whatsapp" className="mt-8">
        <TabsList data-testid="ingestion-tabs" className="bg-white border border-sg-line rounded-full p-1 flex-wrap h-auto">
          <TabsTrigger value="whatsapp" className="rounded-full" data-testid="tab-whatsapp">WhatsApp Paste</TabsTrigger>
          <TabsTrigger value="ocr" className="rounded-full" data-testid="tab-ocr">Floor Plan OCR</TabsTrigger>
          <TabsTrigger value="magic" className="rounded-full" data-testid="tab-magic">Magic Drop</TabsTrigger>
          <TabsTrigger value="builder-url" className="rounded-full" data-testid="tab-builder-url">Builder URL</TabsTrigger>
          <TabsTrigger value="library" className="rounded-full" data-testid="tab-library">My Listings ({listings.length})</TabsTrigger>
        </TabsList>

        {/* WhatsApp */}
        <TabsContent value="whatsapp" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="rounded-3xl bg-sg-navy text-white p-7 font-mono text-sm">
              <div className="overline text-sg-gold mb-3">RAW · FORWARDED MESSAGE</div>
              <Textarea
                value={rawText}
                onChange={(e) => setRawText(e.target.value)}
                rows={14}
                placeholder={"E.g.:\n*Available* 2.5 BHK in Baner\n850 sqft carpet, 12th floor\nReady to move\nAsking 1.45 Cr negotiable\nContact: Rohit 98••• ••456\nFlat A-1204"}
                className="bg-transparent border-white/20 text-white font-mono placeholder:text-white/30 rounded-2xl"
                data-testid="whatsapp-paste-input"
              />
              <Button
                onClick={extract}
                disabled={extracting}
                className="mt-4 w-full rounded-full bg-sg-gold hover:bg-sg-gold-soft text-sg-navy h-11"
                data-testid="whatsapp-extract-btn"
              >
                {extracting ? "Extracting via Claude Sonnet 4.5…" : "Extract → Create Draft"}
              </Button>
            </div>
            <div className="rounded-3xl bg-white border border-sg-line p-7">
              <div className="overline text-sg-gold-deep mb-3">EXTRACTED · ZERO-PII</div>
              {!extracted ? (
                <div className="text-sm text-sg-mute py-12 text-center">Awaiting extraction…</div>
              ) : (
                <ExtractedPreview data={extracted} />
              )}
            </div>
          </div>
        </TabsContent>

        {/* OCR */}
        <TabsContent value="ocr" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="rounded-3xl bg-white border border-sg-line p-10 text-center">
              <Upload className="w-10 h-10 mx-auto text-sg-mute" />
              <p className="font-display text-2xl mt-4 text-sg-navy">Upload floor plan</p>
              <p className="text-sm text-sg-mute mt-1">PNG / JPEG / WEBP · auto-purges in 1 hour</p>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={uploadOcr} className="hidden" data-testid="ocr-file-input" />
              <Button onClick={() => fileRef.current?.click()} disabled={ocrLoading} className="mt-6 rounded-full bg-sg-navy hover:bg-sg-navy-700 text-white" data-testid="ocr-upload-btn">
                {ocrLoading ? "Analysing…" : "Choose Image"}
              </Button>
            </div>
            <div className="rounded-3xl bg-white border border-sg-line p-7">
              <div className="overline text-sg-gold-deep mb-3">EXTRACTED LAYOUT</div>
              {!ocrResult ? (
                <div className="text-sm text-sg-mute py-12 text-center">Awaiting floor plan…</div>
              ) : (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between border-b border-dashed border-sg-line py-1.5"><span className="text-sg-mute">Layout</span><span className="font-medium text-sg-navy">{ocrResult.extracted?.layout || "—"}</span></div>
                  <div className="flex justify-between border-b border-dashed border-sg-line py-1.5"><span className="text-sg-mute">Carpet Area</span><span className="font-mono text-sg-navy">{ocrResult.extracted?.carpet_area_sqft || "—"} sqft</span></div>
                  <div className="flex justify-between border-b border-dashed border-sg-line py-1.5"><span className="text-sg-mute">Built-up Area</span><span className="font-mono text-sg-navy">{ocrResult.extracted?.built_up_area_sqft || "—"} sqft</span></div>
                  <div className="border-b border-dashed border-sg-line py-1.5 text-sg-navy"><span className="text-sg-mute">Rooms · </span>{ocrResult.extracted?.rooms?.join(", ") || "—"}</div>
                  <div className="py-1.5 text-sg-navy"><span className="text-sg-mute">Notes · </span>{ocrResult.extracted?.notes}</div>
                  <p className="overline text-sg-mute mt-3">VOLATILE · TTL EXPIRES {new Date(ocrResult.ttl_expires_at).toLocaleTimeString()}</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Magic Drop */}
        <TabsContent value="magic" className="mt-6">
          <MagicDropPanel />
        </TabsContent>

        {/* Builder URL — Track 1 */}
        <TabsContent value="builder-url" className="mt-6">
          <BuilderURLPanel onImported={reload} />
        </TabsContent>

        {/* Library */}
        <TabsContent value="library" className="mt-6">
          {listings.length === 0 ? (
            <div className="text-sm text-sg-mute py-16 text-center border border-dashed border-sg-line rounded-3xl">
              No listings yet. Paste a WhatsApp blast or upload a floor plan to begin.
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="relative flex-1 min-w-[220px] max-w-md">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-sg-mute pointer-events-none" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search BHK · locality · variant · RERA…"
                    className="pl-9 h-10 rounded-full bg-white border-sg-line"
                    data-testid="library-search-input"
                  />
                </div>
                {availableVariants.length > 0 && (
                  <div className="flex items-center gap-1 bg-sg-cream-soft border border-sg-line rounded-full p-1 flex-wrap" data-testid="library-variant-filter">
                    <span className="text-xs text-sg-mute px-3 flex items-center gap-1"><Layers className="w-3.5 h-3.5" /> Variant</span>
                    <button
                      onClick={() => setVariantFilter("All")}
                      className={`text-xs px-3 py-1.5 rounded-full transition-colors ${variantFilter === "All" ? "bg-sg-navy text-white" : "text-sg-navy hover:bg-white"}`}
                      data-testid="library-variant-pill-all"
                    >All</button>
                    {availableVariants.map((v) => (
                      <button
                        key={v}
                        onClick={() => setVariantFilter(v)}
                        className={`text-xs px-3 py-1.5 rounded-full transition-colors ${variantFilter === v ? "bg-sg-navy text-white" : "text-sg-navy hover:bg-white"}`}
                        data-testid={`library-variant-pill-${v.toLowerCase().replace(/\s+/g, "-")}`}
                      >{v}</button>
                    ))}
                  </div>
                )}
              </div>
              {visibleListings.length === 0 ? (
                <div className="text-sm text-sg-mute py-12 text-center border border-dashed border-sg-line rounded-3xl" data-testid="library-no-results">
                  No listings match the current filter.
                </div>
              ) : (
              <div className="space-y-4">
                {visibleListings.map((l) => (
                <div key={l.listing_id} className="bg-white border border-sg-line rounded-3xl p-6 relative" data-testid={`listing-row-${l.listing_id}`}>
                  {canEditRow(l) && (
                    <div className="absolute top-4 right-4 z-10">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="w-9 h-9 rounded-full border border-sg-line bg-white text-sg-navy hover:bg-sg-cream-soft flex items-center justify-center transition-colors"
                            aria-label="Listing actions"
                            data-testid={`listing-menu-trigger-${l.listing_id}`}
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40" data-testid={`listing-menu-${l.listing_id}`}>
                          <DropdownMenuItem
                            onSelect={() => openEdit(l)}
                            data-testid={`listing-menu-edit-${l.listing_id}`}
                          >
                            <Pencil className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onSelect={() => setPendingDelete(l)}
                            className="text-rose-600 focus:text-rose-700 focus:bg-rose-50"
                            data-testid={`listing-menu-delete-${l.listing_id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                  <div className="flex flex-wrap items-start justify-between gap-3 pr-12">
                    <div>
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Badge variant="outline" className="rounded-full border-sg-line text-sg-mute overline">{l.extraction_source}</Badge>
                        {l.published ? (
                          <Badge className="rounded-full bg-sg-navy text-white">Published</Badge>
                        ) : (
                          <Badge className="rounded-full bg-sg-gold/20 text-sg-gold-deep border border-sg-gold/40">Draft</Badge>
                        )}
                        {l.unit_variant && (
                          <Badge className="rounded-full bg-sg-cream-soft text-sg-navy border border-sg-gold/40" data-testid={`variant-chip-${l.listing_id}`}>
                            <Layers className="w-3 h-3 mr-1 text-sg-gold-deep" /> {l.unit_variant}
                          </Badge>
                        )}
                        {isOwner && l._agent_name && (
                          <Badge className="rounded-full bg-sg-navy/5 text-sg-navy border border-sg-navy/10" data-testid={`listing-agent-chip-${l.listing_id}`}>
                            <User className="w-3 h-3 mr-1" /> {l._agent_name}
                          </Badge>
                        )}
                      </div>
                      <h4 className="font-display text-2xl text-sg-navy">
                        {l.bhk} {l.property_type} <span className="text-sg-mute">· {l.locality}</span>
                      </h4>
                      <p className="text-sm text-sg-mute mt-1">{l.notes}</p>
                    </div>
                    <div className="text-right">
                      <div className="rupee text-3xl text-sg-navy">{formatPrice(l.price_lakhs)}</div>
                      {l.carpet_area_sqft && <div className="text-xs text-sg-mute font-mono">{l.carpet_area_sqft} sqft carpet</div>}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    {(l.redacted_pii_types || []).length > 0 ? (
                      l.redacted_pii_types.map((t) => (
                        <Badge key={t} className="rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <ShieldCheck className="w-3 h-3 mr-1" /> Redacted: {t}
                        </Badge>
                      ))
                    ) : (
                      <Badge className="rounded-full bg-sg-cream-soft text-sg-mute border border-sg-line">
                        <ShieldCheck className="w-3 h-3 mr-1" /> No PII detected
                      </Badge>
                    )}
                    {l.construction_status && l.construction_status !== "UNKNOWN" && (
                      <Badge className="rounded-full bg-sg-navy text-sg-gold" data-testid={`construction-badge-${l.listing_id}`}>
                        {l.construction_status.replace(/_/g, " ")}
                      </Badge>
                    )}
                    {(l.extraction_source === "builder_url" || l.source_url) && (
                      <Badge className="rounded-full bg-sg-cream-soft text-sg-navy border border-sg-gold/40" title={l.source_url || ""}>
                        <LinkIcon className="w-3 h-3 mr-1" /> Builder URL
                      </Badge>
                    )}
                  </div>

                  {l.published && (
                    <div className="mt-4 border-t border-dashed border-sg-line pt-4 flex flex-wrap items-center gap-3">
                      <Badge
                        className={`rounded-full ${l.visibility_scope === "Portal_Global"
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : "bg-sg-cream-soft text-sg-mute border border-sg-line"}`}
                        data-testid={`visibility-badge-${l.listing_id}`}
                      >
                        {l.visibility_scope === "Portal_Global" ? (
                          <><Globe2 className="w-3 h-3 mr-1" /> Portal Global · on /buy</>
                        ) : (
                          <><Lock className="w-3 h-3 mr-1" /> Agent Private · on my portal only</>
                        )}
                      </Badge>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleVisibility(l)}
                        className="rounded-full border-sg-line"
                        data-testid={`visibility-toggle-${l.listing_id}`}
                      >
                        {l.visibility_scope === "Portal_Global"
                          ? "Make Agent Private"
                          : "Promote to Portal Global"}
                      </Button>
                    </div>
                  )}

                  {!l.published && (
                    <div className="mt-4 border-t border-dashed border-sg-line pt-4 flex flex-col gap-3">
                      <label className="flex items-start gap-3 text-sm cursor-pointer">
                        <Checkbox
                          id={`consent-${l.listing_id}`}
                          checked={!!consent[l.listing_id]}
                          onCheckedChange={(v) => setConsent({ ...consent, [l.listing_id]: !!v })}
                          data-testid={`consent-checkbox-${l.listing_id}`}
                        />
                        <span><strong>I confirm I have owner consent</strong> and no unauthorized personal data is present.</span>
                      </label>
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          onClick={() => publish(l.listing_id)}
                          disabled={!consent[l.listing_id]}
                          className="rounded-full bg-sg-navy hover:bg-sg-navy-700 text-white"
                          data-testid={`publish-btn-${l.listing_id}`}
                        >
                          <MessageCircle className="w-4 h-4 mr-1 text-sg-gold" /> Publish to Branded Portal
                        </Button>
                        <Button variant="outline" className="rounded-full border-sg-line" onClick={() => remove(l.listing_id)} data-testid={`delete-btn-${l.listing_id}`}>
                          <Trash2 className="w-4 h-4 mr-1" /> Discard
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* ⋮ EDIT DIALOG ─────────────────────────────────────────────── */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) closeEdit(); }}>
        <DialogContent className="max-w-2xl" data-testid="listing-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Edit listing</DialogTitle>
            <DialogDescription>
              {editing?.published
                ? "This listing is already published — changes go live immediately."
                : "Draft edits stay private until you publish from the card."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <div>
              <Label>Property type</Label>
              <Input value={editForm.property_type || ""} onChange={(e) => setEditForm({ ...editForm, property_type: e.target.value })} data-testid="listing-edit-property-type" />
            </div>
            <div>
              <Label>BHK</Label>
              <Input value={editForm.bhk || ""} onChange={(e) => setEditForm({ ...editForm, bhk: e.target.value })} placeholder="e.g. 2 BHK" data-testid="listing-edit-bhk" />
            </div>
            <div>
              <Label>Price (lakhs)</Label>
              <Input type="number" step="0.01" value={editForm.price_lakhs ?? ""} onChange={(e) => setEditForm({ ...editForm, price_lakhs: e.target.value })} data-testid="listing-edit-price" />
            </div>
            <div>
              <Label>Locality</Label>
              <Input value={editForm.locality || ""} onChange={(e) => setEditForm({ ...editForm, locality: e.target.value })} data-testid="listing-edit-locality" />
            </div>
            <div>
              <Label>Unit variant <span className="text-sg-mute font-normal">(optional)</span></Label>
              <Input value={editForm.unit_variant || ""} onChange={(e) => setEditForm({ ...editForm, unit_variant: e.target.value })} placeholder="e.g. Grand, Duplex" data-testid="listing-edit-variant" />
            </div>
            <div>
              <Label>Carpet area (sqft)</Label>
              <Input type="number" value={editForm.carpet_area_sqft ?? ""} onChange={(e) => setEditForm({ ...editForm, carpet_area_sqft: e.target.value })} data-testid="listing-edit-carpet" />
            </div>
            <div className="md:col-span-2">
              <Label>Construction status</Label>
              <select
                value={editForm.construction_status || ""}
                onChange={(e) => setEditForm({ ...editForm, construction_status: e.target.value })}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="listing-edit-construction-status"
              >
                <option value="">— unchanged —</option>
                <option value="READY_TO_MOVE">Ready to Move</option>
                <option value="UNDER_CONSTRUCTION">Under Construction</option>
                <option value="PRE_LAUNCH">Pre-Launch</option>
                <option value="RESALE">Resale</option>
                <option value="UNKNOWN">Unknown</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <Label>Notes</Label>
              <Textarea value={editForm.notes || ""} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows={3} data-testid="listing-edit-notes" />
            </div>
          </div>
          <DialogFooter className="mt-4 gap-2">
            <Button variant="outline" onClick={closeEdit} disabled={savingEdit} data-testid="listing-edit-cancel">Cancel</Button>
            <Button onClick={saveEdit} disabled={savingEdit} className="bg-sg-navy text-white hover:bg-sg-navy-700" data-testid="listing-edit-save">
              {savingEdit ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ⋮ DELETE CONFIRM ─────────────────────────────────────────── */}
      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => { if (!o) setPendingDelete(null); }}>
        <AlertDialogContent data-testid="listing-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this listing?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete?.published
                ? "This is a published listing. Deleting will remove it from your portal and the public /buy marketplace instantly. This can't be undone."
                : "This draft will be removed. This can't be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingBusy} data-testid="listing-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deletingBusy}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="listing-delete-confirm"
            >
              {deletingBusy ? "Deleting…" : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardShell>
  );
}

function ExtractedPreview({ data }) {
  const fields = [
    ["Property Type", data.property_type],
    ["BHK", data.bhk],
    ["Price", data.price_lakhs ? formatPrice(data.price_lakhs) : "—"],
    ["Locality", data.locality],
    ["Status", data.status],
    ["Carpet area", data.carpet_area_sqft ? `${data.carpet_area_sqft} sqft` : "—"],
    ["Land size", data.land_size_gunthas ? `${data.land_size_gunthas} guntha` : "—"],
  ];
  return (
    <div>
      <div className="space-y-1.5 text-sm">
        {fields.map(([k, v]) => (
          <div key={k} className="flex justify-between border-b border-dashed border-sg-line py-1.5">
            <span className="text-sg-mute">{k}</span>
            <span className="font-medium text-sg-navy">{v || "—"}</span>
          </div>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {(data.redacted_pii_types || []).length > 0 ? (
          data.redacted_pii_types.map((t) => (
            <Badge key={t} className="rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`pii-badge-${t}`}>
              <ShieldCheck className="w-3 h-3 mr-1" /> PII Redacted: {t}
            </Badge>
          ))
        ) : (
          <Badge className="rounded-full bg-sg-cream-soft text-sg-mute border border-sg-line">No PII detected</Badge>
        )}
      </div>
      <p className="text-xs text-sg-mute mt-3">
        Draft saved. Open <strong>My Listings</strong> tab → confirm consent → Publish.
      </p>
    </div>
  );
}
